<template>
  <div class="data-table-container">
    <!-- Header com filtros e ações -->
    <div class="table-header">
      <div class="header-left">
        <h3 class="table-title">
          <i :class="titleIcon"></i>
          {{ title }}
        </h3>
        <span class="table-subtitle" v-if="subtitle">{{ subtitle }}</span>
      </div>
      <div class="header-right">
        <div class="table-actions">
          <slot name="header-actions"></slot>
        </div>
      </div>
    </div>

    <!-- Filtros -->
    <div v-if="showFilters" class="table-filters">
      <div class="filters-row">
        <div class="filter-group">
          <label class="filter-label">Buscar</label>
          <div class="input-group">
            <span class="input-group-text">
              <i class="fas fa-search"></i>
            </span>
            <input
              v-model="filters.search"
              type="text"
              class="form-control"
              :placeholder="searchPlaceholder"
              @input="debounceSearch"
            />
          </div>
        </div>

        <div v-for="filter in customFilters" :key="filter.key" class="filter-group">
          <label class="filter-label">{{ filter.label }}</label>
          <select
            v-model="filters[filter.key]"
            class="form-select"
            @change="applyFilters"
          >
            <option value="">{{ filter.placeholder || 'Todos' }}</option>
            <option v-for="option in filter.options" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <div class="filter-actions">
          <button @click="clearFilters" class="btn btn-outline-secondary btn-sm">
            <i class="fas fa-times"></i>
            Limpar
          </button>
          <button @click="exportData" class="btn btn-outline-primary btn-sm" v-if="showExport">
            <i class="fas fa-download"></i>
            Exportar
          </button>
        </div>
      </div>
    </div>

    <!-- Tabela -->
    <div class="table-wrapper">
      <div v-if="loading" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i>
        <span>Carregando dados...</span>
      </div>

      <div v-else-if="data.length === 0" class="empty-state">
        <i :class="emptyIcon"></i>
        <h4>{{ emptyTitle }}</h4>
        <p>{{ emptyMessage }}</p>
        <slot name="empty-actions"></slot>
      </div>

      <div v-else class="table-responsive">
        <table class="table">
          <thead>
            <tr>
              <th v-for="column in columns" :key="column.key" :class="column.class">
                <div class="th-content">
                  <span>{{ column.label }}</span>
                  <button
                    v-if="column.sortable"
                    @click="sort(column.key)"
                    class="sort-btn"
                    :class="{ active: sortField === column.key }"
                  >
                    <i :class="getSortIcon(column.key)"></i>
                  </button>
                </div>
              </th>
              <th v-if="showActions" class="actions-column">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in paginatedData" :key="getItemKey(item, index)">
              <td v-for="column in columns" :key="column.key" :class="column.class">
                <slot :name="`cell-${column.key}`" :item="item" :value="getNestedValue(item, column.key)">
                  <span v-if="column.type === 'date'">{{ formatDate(getNestedValue(item, column.key)) }}</span>
                  <span v-else-if="column.type === 'currency'">{{ formatCurrency(getNestedValue(item, column.key)) }}</span>
                  <span v-else-if="column.type === 'badge'">
                    <span class="badge" :class="getBadgeClass(item, column)">
                      {{ getNestedValue(item, column.key) }}
                    </span>
                  </span>
                  <span v-else>{{ getNestedValue(item, column.key) }}</span>
                </slot>
              </td>
              <td v-if="showActions" class="actions-column">
                <div class="action-buttons">
                  <slot name="actions" :item="item" :index="index">
                    <button
                      v-if="showView"
                      @click="$emit('view', item)"
                      class="btn-action btn-view"
                      title="Visualizar"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                    <button
                      v-if="showEdit"
                      @click="$emit('edit', item)"
                      class="btn-action btn-edit"
                      title="Editar"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                    <button
                      v-if="showDelete"
                      @click="confirmDelete(item)"
                      class="btn-action btn-delete"
                      title="Excluir"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </slot>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Paginação -->
    <div v-if="showPagination && totalPages > 1" class="table-pagination">
      <div class="pagination-info">
        <span>
          Mostrando {{ (currentPage - 1) * itemsPerPage + 1 }} a 
          {{ Math.min(currentPage * itemsPerPage, totalItems) }} de 
          {{ totalItems }} registros
        </span>
      </div>
      <div class="pagination-controls">
        <button
          @click="goToPage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="btn btn-outline-secondary btn-sm"
        >
          <i class="fas fa-chevron-left"></i>
          Anterior
        </button>
        
        <div class="page-numbers">
          <button
            v-for="page in visiblePages"
            :key="page"
            @click="goToPage(page)"
            :class="['btn', 'btn-sm', page === currentPage ? 'btn-primary' : 'btn-outline-primary']"
          >
            {{ page }}
          </button>
        </div>
        
        <button
          @click="goToPage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="btn btn-outline-secondary btn-sm"
        >
          Próximo
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DataTable',
  props: {
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: ''
    },
    titleIcon: {
      type: String,
      default: 'fas fa-table'
    },
    data: {
      type: Array,
      default: () => []
    },
    columns: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    showFilters: {
      type: Boolean,
      default: true
    },
    showActions: {
      type: Boolean,
      default: true
    },
    showView: {
      type: Boolean,
      default: true
    },
    showEdit: {
      type: Boolean,
      default: true
    },
    showDelete: {
      type: Boolean,
      default: true
    },
    showPagination: {
      type: Boolean,
      default: true
    },
    showExport: {
      type: Boolean,
      default: true
    },
    itemsPerPage: {
      type: Number,
      default: 10
    },
    customFilters: {
      type: Array,
      default: () => []
    },
    searchPlaceholder: {
      type: String,
      default: 'Digite para buscar...'
    },
    emptyTitle: {
      type: String,
      default: 'Nenhum registro encontrado'
    },
    emptyMessage: {
      type: String,
      default: 'Não há dados para exibir no momento.'
    },
    emptyIcon: {
      type: String,
      default: 'fas fa-inbox'
    },
    itemKey: {
      type: String,
      default: 'id'
    }
  },
  data() {
    return {
      filters: {
        search: '',
        ...this.customFilters.reduce((acc, filter) => {
          acc[filter.key] = ''
          return acc
        }, {})
      },
      currentPage: 1,
      sortField: '',
      sortDirection: 'asc',
      searchTimeout: null
    }
  },
  computed: {
    filteredData() {
      let filtered = [...this.data]

      // Aplicar filtro de busca
      if (this.filters.search) {
        const search = this.filters.search.toLowerCase()
        filtered = filtered.filter(item => {
          return this.columns.some(column => {
            const value = this.getNestedValue(item, column.key)
            return value && value.toString().toLowerCase().includes(search)
          })
        })
      }

      // Aplicar filtros customizados
      this.customFilters.forEach(filter => {
        if (this.filters[filter.key]) {
          filtered = filtered.filter(item => {
            const value = this.getNestedValue(item, filter.key)
            return value === this.filters[filter.key]
          })
        }
      })

      // Aplicar ordenação
      if (this.sortField) {
        filtered.sort((a, b) => {
          const aValue = this.getNestedValue(a, this.sortField)
          const bValue = this.getNestedValue(b, this.sortField)
          
          if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1
          if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1
          return 0
        })
      }

      return filtered
    },
    paginatedData() {
      if (!this.showPagination) return this.filteredData
      
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredData.slice(start, end)
    },
    totalItems() {
      return this.filteredData.length
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage)
    },
    visiblePages() {
      const pages = []
      const maxVisible = 5
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2))
      let end = Math.min(this.totalPages, start + maxVisible - 1)
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1)
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      
      return pages
    }
  },
  methods: {
    getNestedValue(obj, path) {
      return path.split('.').reduce((current, key) => current?.[key], obj)
    },
    getItemKey(item, index) {
      return this.getNestedValue(item, this.itemKey) || index
    },
    sort(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortField = field
        this.sortDirection = 'asc'
      }
    },
    getSortIcon(field) {
      if (this.sortField !== field) return 'fas fa-sort'
      return this.sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'
    },
    getBadgeClass(item, column) {
      if (column.badgeClass) {
        return typeof column.badgeClass === 'function' 
          ? column.badgeClass(item) 
          : column.badgeClass
      }
      return 'badge-secondary'
    },
    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleDateString('pt-BR')
    },
    formatCurrency(value) {
      if (!value) return 'R$ 0,00'
      return `R$ ${parseFloat(value).toFixed(2).replace('.', ',')}`
    },
    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.applyFilters()
      }, 300)
    },
    applyFilters() {
      this.currentPage = 1
      this.$emit('filter', this.filters)
    },
    clearFilters() {
      this.filters = {
        search: '',
        ...this.customFilters.reduce((acc, filter) => {
          acc[filter.key] = ''
          return acc
        }, {})
      }
      this.currentPage = 1
      this.$emit('filter', this.filters)
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
        this.$emit('page-change', page)
      }
    },
    confirmDelete(item) {
      this.$sweet.showConfirm(
        'Confirmar Exclusão',
        'Tem certeza que deseja excluir este item?',
        'Excluir'
      ).then((result) => {
        if (result.isConfirmed) {
          this.$emit('delete', item)
        }
      })
    },
    exportData() {
      this.$emit('export', this.filteredData)
    }
  }
}
</script>

<style lang="scss" scoped>
.data-table-container {
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.table-header {
  padding: 25px 30px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .header-left {
    .table-title {
      margin: 0 0 5px 0;
      font-size: 20px;
      font-weight: 600;
      color: #2c3e50;
      display: flex;
      align-items: center;
      gap: 10px;

      i {
        color: #667eea;
      }
    }

    .table-subtitle {
      font-size: 14px;
      color: #6c757d;
    }
  }

  .header-right {
    .table-actions {
      display: flex;
      gap: 10px;
    }
  }
}

.table-filters {
  padding: 20px 30px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;

  .filters-row {
    display: grid;
    grid-template-columns: 2fr repeat(auto-fit, minmax(200px, 1fr)) auto;
    gap: 20px;
    align-items: end;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .filter-group {
    .filter-label {
      display: block;
      font-weight: 600;
      color: #495057;
      margin-bottom: 8px;
      font-size: 14px;
    }

    .input-group {
      display: flex;

      .input-group-text {
        background: white;
        border: 1px solid #ced4da;
        border-right: none;
        border-radius: 6px 0 0 6px;
        padding: 8px 12px;
        color: #6c757d;
      }

      .form-control {
        border-radius: 0 6px 6px 0;
        border-left: none;
      }
    }

    .form-control,
    .form-select {
      border: 1px solid #ced4da;
      border-radius: 6px;
      padding: 8px 12px;
      font-size: 14px;
      transition: border-color 0.3s ease, box-shadow 0.3s ease;

      &:focus {
        outline: none;
        border-color: #667eea;
        box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
      }
    }
  }

  .filter-actions {
    display: flex;
    gap: 10px;
  }
}

.table-wrapper {
  .loading-state,
  .empty-state {
    padding: 60px 30px;
    text-align: center;
    color: #6c757d;

    i {
      font-size: 48px;
      margin-bottom: 20px;
      opacity: 0.5;
    }

    h4 {
      margin: 0 0 10px 0;
      color: #495057;
    }

    p {
      margin: 0 0 20px 0;
    }
  }
}

.table-responsive {
  overflow-x: auto;

  .table {
    width: 100%;
    border-collapse: collapse;
    margin: 0;

    th,
    td {
      padding: 15px 12px;
      text-align: left;
      border-bottom: 1px solid #e9ecef;
      vertical-align: middle;
    }

    th {
      background: #f8f9fa;
      font-weight: 600;
      color: #495057;
      font-size: 14px;
      position: sticky;
      top: 0;
      z-index: 10;

      .th-content {
        display: flex;
        align-items: center;
        gap: 8px;

        .sort-btn {
          background: none;
          border: none;
          color: #6c757d;
          cursor: pointer;
          padding: 2px;
          border-radius: 3px;
          transition: all 0.3s ease;

          &:hover {
            color: #495057;
            background: rgba(0, 0, 0, 0.05);
          }

          &.active {
            color: #667eea;
          }
        }
      }
    }

    td {
      font-size: 14px;
    }

    .actions-column {
      width: 120px;
      text-align: center;

      .action-buttons {
        display: flex;
        gap: 5px;
        justify-content: center;

        .btn-action {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          font-size: 14px;

          &.btn-view {
            background: #e3f2fd;
            color: #1976d2;

            &:hover {
              background: #bbdefb;
            }
          }

          &.btn-edit {
            background: #f3e5f5;
            color: #7b1fa2;

            &:hover {
              background: #e1bee7;
            }
          }

          &.btn-delete {
            background: #ffebee;
            color: #d32f2f;

            &:hover {
              background: #ffcdd2;
            }
          }
        }
      }
    }

    tbody tr {
      transition: background-color 0.3s ease;

      &:hover {
        background-color: rgba(102, 126, 234, 0.05);
      }
    }
  }
}

.table-pagination {
  padding: 20px 30px;
  border-top: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8f9fa;

  .pagination-info {
    font-size: 14px;
    color: #6c757d;
  }

  .pagination-controls {
    display: flex;
    align-items: center;
    gap: 10px;

    .page-numbers {
      display: flex;
      gap: 5px;
    }

    .btn {
      border-radius: 6px;
      font-size: 14px;
      padding: 6px 12px;
      transition: all 0.3s ease;

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}

.badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;

  &.badge-secondary {
    background: rgba(108, 117, 125, 0.1);
    color: #6c757d;
  }

  &.badge-success {
    background: rgba(40, 167, 69, 0.1);
    color: #28a745;
  }

  &.badge-danger {
    background: rgba(220, 53, 69, 0.1);
    color: #dc3545;
  }

  &.badge-warning {
    background: rgba(255, 193, 7, 0.1);
    color: #ffc107;
  }

  &.badge-info {
    background: rgba(23, 162, 184, 0.1);
    color: #17a2b8;
  }
}

@media (max-width: 768px) {
  .table-header {
    padding: 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .table-filters {
    padding: 15px 20px;
  }

  .table-pagination {
    padding: 15px 20px;
    flex-direction: column;
    gap: 15px;
    align-items: stretch;

    .pagination-controls {
      justify-content: center;
    }
  }
}
</style>
